import React, { useEffect, useState } from 'react';
import { pendingDirectRecordings, resumeDirectRecording, startRecordingRecovery } from '../../utils/directRecordingUpload';
import { recordingUiError } from '../../utils/recordingControls';

export default function DirectRecordingStatus({ roomId }) {
  useEffect(() => startRecordingRecovery(), []);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  useEffect(() => {
    const show = () => setOpen(true);
    const escape = event => { if (event.key === 'Escape') setOpen(false); };
    window.addEventListener('meeting-recording-uploads-open', show);
    window.addEventListener('keydown', escape);
    return () => { window.removeEventListener('meeting-recording-uploads-open', show); window.removeEventListener('keydown', escape); };
  }, []);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let mounted = true;
    const refresh = () => pendingDirectRecordings(roomId).then(rows => { if (mounted) setPending(rows); }).catch(() => {});
    const change = event => {
      if (!event?.detail || (roomId && event.detail.roomId !== roomId)) return;
      setStatus(event.detail);
    };
    window.addEventListener('meeting-recording-direct-status', change);
    if (open) window.addEventListener('online', refresh);
    const timer = open ? window.setInterval(refresh, 15000) : null;
    if (open) void refresh();
    return () => { mounted = false; window.clearInterval(timer); window.removeEventListener('meeting-recording-direct-status', change); window.removeEventListener('online', refresh); };
  }, [roomId, open]);
  const resume = async row => {
    setBusy(row.id); setError('');
    try { await resumeDirectRecording(row.id); setPending(await pendingDirectRecordings(roomId)); }
    catch (failure) { setError(recordingUiError(failure)); }
    finally { setBusy(''); }
  };
  if (!open) return null;
  const percentage = Math.max(0, Math.min(100, Number(status?.percentage) || 0));
  return <aside className="meeting-recording-upload fixed bottom-24 right-3 z-50 max-w-[calc(100vw-1.5rem)] max-h-[60dvh] overflow-y-auto w-80 rounded-xl border border-white/20 bg-slate-950/95 p-3 text-white shadow-xl" aria-label="Recording upload">
    <div className="flex items-center justify-between gap-3"><div className="text-sm font-semibold">Recording uploads</div><button type="button" onClick={() => setOpen(false)} className="rounded px-3 py-2 hover:bg-white/10" aria-label="Close recording uploads">✕</button></div>
    {!pending.length && (!status || status.state === 'completed') && <p className="mt-2 text-xs">No uploads need recovery.</p>}
    {status && status.state !== 'completed' && <div className="mt-1 text-xs text-slate-200" role="status">
      {status.state === 'verifying' ? 'Upload complete · Verifying storage' : status.state === 'paused' ? status.captureEnded ? 'Save paused · Kept in this browser' : 'Recording continues · Upload will retry' : status.captureEnded || status.state === 'uploading' ? 'Saving recording' : 'Recording'}
      <progress className="mt-2 w-full" value={percentage} max="100" aria-label="Recorded bytes saved" />
      <span>{percentage}% saved. Keep this tab open.</span>
      {status.message && <p className="mt-2 text-amber-200">{recordingUiError({ message: status.message })}</p>}
    </div>}
    {pending.length > 0 && <div className="mt-3 space-y-2">
      <p className="text-xs text-amber-200">Saved recordings need upload recovery. An interrupted recording may be missing its last moments.</p>
      {pending.map(row => <button key={row.id} type="button" disabled={Boolean(busy)} onClick={() => resume(row)} className="min-h-[44px] w-full rounded-lg bg-indigo-600 px-3 py-2 text-left text-xs disabled:opacity-50">
        {busy === row.id ? 'Resuming…' : row.recoverable ? 'Resume upload' : 'Check migrated upload'} · {row.title || 'Class recording'}
        <span className="block opacity-75">{new Date(row.createdAt).toLocaleString()}</span>
      </button>)}
    </div>}
    {error && <p role="alert" className="mt-2 text-xs text-red-200">{error}</p>}
  </aside>;
}
